import { skipToken } from '@reduxjs/toolkit/query'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useGetAuthoringComponentQuery } from '../api/authoring'
import type { Document, Manifest } from '../api/authoring'
import { problemMessage } from '../api/baseApi'
import { arranges, composeDocument, type CompositionNode } from '../model/composition'
import { isImage, thumbnail } from '../model/derived'
import { acceptsUpload, proseOf, typeOf } from '../model/manifest'
import { boundReferences, markup, plainParagraphs } from '../model/rendered'
import { ownedReferences } from '../model/records'
import { declaredReferences } from '../model/references'
import { bodyEdges, patternEdges, says, type Said } from '../model/says'
import { Loading } from './QueryState'

/**
 * The page as a reader meets it, composed from what this client already
 * holds.
 *
 * **It is a reading, not a rendering of the site.** `SitePreview` frames
 * the real front-end serving the real publication, which is the only
 * thing that can answer "what does it look like"; it cannot answer "what
 * does it say right now", because an authored edit is absent from it
 * until a release captures it. This answers the second question and
 * deliberately not the first: content in reading order, in the admin's
 * own type, with none of the site's styling. Two things that both
 * claimed to show the page would be two things an author has to learn to
 * distrust differently.
 *
 * That is also why nothing here is a house style for pages. A heading is
 * the admin's heading, an image is never drawn past its own size: the
 * moment this starts approximating a theme it becomes a second renderer
 * whose disagreements with the real one are bugs somebody has to chase.
 *
 * **It is composed client-side, from the documents on screen.** The
 * platform's composer is the one that expands patterns, resolves
 * repeaters and draws generated pages, and it runs at publish. This walks
 * the stored tree instead — the same walk `CompositionEditor` draws as an
 * outline — which is exactly the page's *own* content and nothing that is
 * assembled for it. Where the two differ, the node says so in place
 * rather than leaving a gap the author reads as missing content.
 *
 * **What it leaves out is the manifest's call, never a type's name.** A
 * concealed field is one delivery never serves; a string carrying a
 * placement marker is machinery; a child no arrangement names composes
 * nowhere on the site and so nowhere here — a body's bound pictures are
 * drawn inside the body, where the site draws them. See `model/says`.
 *
 * **A record is read as the page the site makes of it.** It has no
 * regions; what its page says is its own words, its picture, and what it
 * owns — a pest's body, an album's photos — under the pattern `layout`
 * names, which the composer runs at publish and this client does not.
 */
export function ContentPreview({
  tenant,
  manifest,
  document,
  layout,
}: {
  tenant: string
  manifest: Manifest | undefined
  document: Document
  /** For a record: the pattern its generated route lays the page out with. */
  layout?: string
}) {
  const root = useMemo(() => composeDocument(manifest, document), [manifest, document])
  const record = typeOf(manifest, document.type)?.role === 'record'
  const filled = root.regions.filter((region) => region.placements.length > 0).length
  const title = document.label || 'Untitled page'
  const context = useMemo(() => ({ tenant, manifest, title }), [tenant, manifest, title])

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Stack sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h3">What this page says</Typography>
      </Stack>

      {/* A column, because reading is a column. The measure is capped at
          about seventy characters for the same reason a book is: the
          screen is wider than a line of prose wants to be, and a preview
          the full width of a desktop window is harder to read than the
          page it previews. */}
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Box sx={{ maxWidth: '68ch', mx: 'auto' }}>
          <Typography variant="h1" sx={{ mb: 2 }}>
            {title}
          </Typography>

          {/* Empty is decided by what holds content, not by what is
              declared: a page type declares its regions whether or not
              anything is in them, and a page with three empty regions is
              a page with nothing on it. */}
          {record ? (
            <Reading.Provider value={context}>
              <RecordReading root={root} document={document} layout={layout} />
            </Reading.Provider>
          ) : filled === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nothing has been placed on this page yet.
            </Typography>
          ) : (
            <Reading.Provider value={context}>
              <Stack spacing={3}>
                {root.regions.map((region, index) => (
                  <Stack key={`${region.slot ?? ''}-${index}`} spacing={2}>
                    {/* **A region is named only when there is another
                        one.** "Header / Body / Footer" over a page that
                        has content in exactly one of them is a label
                        answering a question nobody asked. Where two
                        regions hold content the names earn their place:
                        "the footer also says this" is part of what the
                        page says. */}
                    {region.slot && region.placements.length > 0 && filled > 1 && (
                      <Typography variant="overline" color="text.disabled">
                        {region.slot}
                      </Typography>
                    )}
                    <Placements nodes={region.placements} depth={0} />
                  </Stack>
                ))}
              </Stack>
            </Reading.Provider>
          )}
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
        This is the page as it is now, including unpublished edits, shown in plain styling rather than the
        site&rsquo;s own design.
      </Typography>
    </Paper>
  )
}

/** What every node in one reading shares. */
const Reading = createContext<{ tenant: string; manifest: Manifest | undefined; title: string }>({
  tenant: '',
  manifest: undefined,
  title: '',
})

/**
 * How far this follows a linked child before it stops.
 *
 * Every level is a fetch per node, so a gallery of eighty photos inside a
 * section inside a page is three levels and eighty-two requests. Four is
 * past anything the seeded sites reach and short of a cycle costing
 * anything — and a link that cycles is legal in the model, so a walk with
 * no floor is a walk that does not end.
 */
const DEPTH = 4

/**
 * A run of placements, stacked — or tiled, when every one is a picture.
 *
 * Whether a placement is a picture is its type's `acceptsUpload`, so a
 * gallery tiles and a section holding a photo between two paragraphs
 * does not. Ten full-width photos one under another is a gallery read as
 * an essay, and it made one site's home page eight thousand pixels long.
 */
function Placements({ nodes, depth, compact }: { nodes: CompositionNode[]; depth: number; compact?: boolean }) {
  const { manifest } = useContext(Reading)
  const tiled = nodes.length > 1 && nodes.every((node) => acceptsUpload(manifest, node.type))

  if (tiled) {
    return (
      <Box sx={tiles(120)}>
        {nodes.map((child) => (
          <PreviewNode key={child.linkId ?? child.path.join('/')} node={child} depth={depth} compact />
        ))}
      </Box>
    )
  }
  return (
    <>
      {nodes.map((child) => (
        <PreviewNode key={child.linkId ?? child.path.join('/')} node={child} depth={depth} compact={compact} />
      ))}
    </>
  )
}

/**
 * One node, and whatever is under it.
 *
 * A linked node's content is in another stream, so it is fetched here
 * rather than read off the page — which is the one place this walk costs
 * a request, and the reason the depth is capped.
 */
function PreviewNode({ node, depth, compact }: { node: CompositionNode; depth: number; compact?: boolean }) {
  if (node.linked && node.targetId) {
    return <Fetched id={node.targetId} typeName={node.type} depth={depth} compact={compact} />
  }
  return <Inline node={node} depth={depth} compact={compact} />
}

/** A component held in another stream, fetched and drawn as an inline node is. */
function Fetched({ id, typeName, depth, compact }: { id: string; typeName: string; depth: number; compact?: boolean }) {
  const { tenant, manifest } = useContext(Reading)
  const target = useGetAuthoringComponentQuery({ tenant, id })
  const node = useMemo(() => (target.data ? composeDocument(manifest, target.data) : undefined), [manifest, target.data])

  if (target.isLoading) return <Loading label="Reading…" />
  if (target.error) {
    return (
      <Alert severity="warning">
        {problemMessage(target.error, 'One of this page’s blocks could not be read.')}
      </Alert>
    )
  }
  if (!target.data || !node) return null
  if (depth >= DEPTH) return <Elided typeName={typeName} />
  return <Inline node={node} depth={depth} document={target.data} compact={compact} />
}

/**
 * What a node says and what it holds.
 *
 * The order is the order a reader meets it in: the node's picture, its
 * own words, where it leads, what is placed inside it, then what it
 * continues into. Nothing about *which* type this is decides any of it —
 * see `model/says` — which is what lets a type this client has never
 * seen draw its content the day a tenant declares it.
 *
 * `compact` is a card in a grid: a record in a repeated list, a photo in
 * a gallery. It says the same things, smaller, and clamps long prose.
 */
function Inline({
  node,
  depth,
  document,
  compact,
}: {
  node: CompositionNode
  depth: number
  /** The whole document, where this node is one — for its derived file. */
  document?: Document
  compact?: boolean
}) {
  const { manifest, title } = useContext(Reading)
  const record = typeOf(manifest, node.type)?.role === 'record'
  // **Only what an arrangement names.** A child nothing arranges composes
  // nowhere on the site, and the ones that matter — a body's bound
  // pictures and links — are drawn inside the body, where they belong.
  // Drawing them again here put every picture of an About page twice.
  const placements = node.regions.flatMap((region) => region.placements)
  // A body's references name their placements by link id, and only the
  // placement says which component that is.
  const bound = useMemo(
    () => Object.fromEntries(node.unarranged.flatMap((child) => (child.linkId && child.targetId ? [[child.linkId, child.targetId]] : []))),
    [node.unarranged],
  )

  const picture = document && isImage(document) ? thumbnail(document, compact ? 480 : 1200) : undefined
  // **A picture says what it shows, and nothing it stores is a
  // sentence.** Read as words, an uploaded file volunteers its storage
  // key, its content type and its checksum. So a node with bytes is its
  // bytes, plus the caption an author wrote to sit under them.
  const { said, targets, repeated } = useMemo(
    () => (picture ? { said: [], targets: [], repeated: false } : says(manifest, node, [title])),
    [picture, manifest, node, title],
  )
  // A repeating block's list, drawn — but not a record's. An album is a
  // record with a gallery, and following *its* list printed every photo
  // of every album under the page that lists them.
  const listed = record ? [] : listedEdges(manifest, node)
  const bodies = bodyEdges(manifest, node)
  const { pattern, records } = patternEdges(manifest, node)
  const cover = picture ? undefined : coverEdge(manifest, node)

  const nothing =
    !picture && !cover && said.length === 0 && placements.length === 0 && listed.length === 0 && bodies.length === 0
  // A node whose only words were already said — a heading repeating the
  // page's title — has nothing new to add, and that is not the same as
  // content this walk cannot compose.
  if (nothing && !pattern) return repeated ? null : <NotComposed typeName={node.type} />

  return (
    <Stack spacing={compact ? 0.75 : 1.5} sx={compact ? { minWidth: 0 } : undefined}>
      {picture && <Picture url={picture} node={node} compact={compact} />}
      {cover?.id && <Cover id={cover.id} compact={compact} />}

      <Words said={said} targets={targets} bound={bound} compact={compact} />

      {depth < DEPTH && <Placements nodes={placements} depth={depth + 1} compact={compact} />}

      {bodies.map((edge) =>
        edge.id ? <Fetched key={edge.edgeId} id={edge.id} typeName={edge.type ?? ''} depth={depth + 1} compact={compact} /> : null,
      )}

      {listed.map((edge) => (
        <Repeated key={edge.edgeId} source={edge.id} depth={depth} pattern={pattern?.id} />
      ))}

      {pattern?.id && listed.length === 0 && <LaidOut pattern={pattern.id} records={records.map((edge) => edge.id)} />}
    </Stack>
  )
}

/**
 * A node's words, set by what each one is.
 *
 * A field the type's `label` names is the block's name and is set as a
 * heading; a node whose fields point at a component is a link, and says
 * where it goes rather than printing the id it holds.
 */
function Words({
  said,
  targets,
  bound,
  compact,
}: {
  said: Said[]
  targets: string[]
  bound: Record<string, string>
  compact?: boolean
}) {
  const clamp = compact ? clamped(4) : undefined

  if (targets.length > 0) {
    const words = said.flatMap((block) => (block.kind === 'text' ? [block.text] : []))
    return (
      <Typography variant="body1" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'baseline' }}>
        <Box component="span" sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'underline' }}>
          {words.join(' ') || 'A link'}
        </Box>
        {targets.map((id) => (
          <Destination key={id} id={id} />
        ))}
      </Typography>
    )
  }

  return (
    <>
      {said.map((block, index) => {
        if (block.kind === 'html') {
          return <Prose key={index} html={block.html} bound={bound} names={block.names} compact={compact} />
        }
        if (block.kind === 'list') {
          return (
            <Box key={index} component="ul" sx={{ my: 0, pl: 3, ...clamp }}>
              {block.items.map((item, line) => (
                <Typography key={line} component="li" variant="body1">
                  {item}
                </Typography>
              ))}
            </Box>
          )
        }
        if (block.names) {
          return (
            <Typography key={index} variant={compact ? 'subtitle2' : 'h3'} sx={{ fontWeight: 650 }}>
              {block.text}
            </Typography>
          )
        }
        return (
          <Box key={index} sx={{ ...proseStyles, ...clamp }}>
            {plainParagraphs(block.text).map((paragraph, line) => (
              <Typography key={line} variant={compact ? 'body2' : 'body1'} sx={{ mb: 1 }}>
                {paragraph}
              </Typography>
            ))}
          </Box>
        )
      })}
    </>
  )
}

/** Where a link goes, named as the component it names. */
function Destination({ id }: { id: string }) {
  const { tenant } = useContext(Reading)
  const target = useGetAuthoringComponentQuery({ tenant, id })
  return (
    <Typography component="span" variant="body2" color="text.secondary">
      &rarr; {target.data?.label || (target.isLoading ? '…' : 'a page that could not be read')}
    </Typography>
  )
}

/**
 * Authored markup, with its bound pictures resolved.
 *
 * The body names a picture by reference; the picture's address is the
 * placed Media's, read one request per picture the way the editor reads
 * it. Until it arrives the picture is left out rather than drawn broken.
 */
function Prose({
  html,
  bound,
  names,
  compact,
}: {
  html: string
  bound: Record<string, string>
  names: boolean
  compact?: boolean
}) {
  const references = useMemo(() => boundReferences(html), [html])
  const [addresses, setAddresses] = useState<Record<string, string>>({})
  const resolved = useCallback((linkId: string, url: string | undefined) => {
    setAddresses((held) => (url && held[linkId] !== url ? { ...held, [linkId]: url } : held))
  }, [])
  // Sanitised in `model/rendered`, which is where the argument for doing
  // it at all is written down. The short version: a body is stored
  // verbatim, scripts included, and running one here would run it with
  // the admin's session.
  const safe = useMemo(
    () =>
      markup(html, (reference) => {
        const [linkId] = reference.slice('cms:'.length).split('@')
        return addresses[linkId]
      }),
    [html, addresses],
  )

  return (
    <>
      {references.map(({ linkId, target }) => {
        const id = bound[linkId] ?? target
        return id ? <Address key={linkId} id={id} linkId={linkId} onResolved={resolved} /> : null
      })}
      <Box
        sx={{
          ...proseStyles,
          ...(names ? { fontWeight: 650, fontSize: compact ? '1rem' : '1.25rem' } : undefined),
          ...(compact
            ? {
                fontSize: names ? '1rem' : '0.875rem',
                // A card's prose is a glimpse, and a heading inside it is
                // a line of it rather than a title over the card.
                '& h1, & h2, & h3, & h4, & h5, & h6': { fontSize: '1rem', my: 0.5 },
                ...clamped(4),
              }
            : undefined),
        }}
        dangerouslySetInnerHTML={{ __html: safe }}
      />
    </>
  )
}

/** One bound picture's address, read and reported upward. */
function Address({
  id,
  linkId,
  onResolved,
}: {
  id: string
  linkId: string
  onResolved: (linkId: string, url: string | undefined) => void
}) {
  const { tenant } = useContext(Reading)
  const { data } = useGetAuthoringComponentQuery({ tenant, id })
  const url = thumbnail(data, 1024)
  useEffect(() => {
    onResolved(linkId, url)
  }, [linkId, url, onResolved])
  return null
}

/**
 * The records a repeating block will draw, drawn as cards.
 *
 * A repeater emits one copy of a pattern per record in a collection, and
 * *what it looks like* is decided at publish — so the shape of the output
 * is genuinely not knowable here. **The records are.** They are tiled
 * rather than stacked: a list of twelve pests at full width, each with
 * its icon drawn three times its size, was ten thousand pixels of what a
 * site shows as a grid.
 */
function Repeated({ source, depth, pattern }: { source: string; depth: number; pattern?: string }) {
  const { tenant, manifest } = useContext(Reading)
  const collection = useGetAuthoringComponentQuery({ tenant, id: source })
  const composed = useMemo(
    () => (collection.data ? composeDocument(manifest, collection.data) : undefined),
    [manifest, collection.data],
  )

  if (collection.isLoading) return <Loading label="Reading the list…" />
  if (collection.error) {
    return (
      <Alert severity="warning">
        {problemMessage(collection.error, 'The list this block repeats could not be read.')}
      </Alert>
    )
  }

  const records = composed?.regions.flatMap((region) => region.placements) ?? []
  if (records.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        This repeating block&rsquo;s list is empty, so it draws nothing.
      </Typography>
    )
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        One for each of these {records.length}
        {pattern ? <PatternName id={pattern} /> : ', laid out when the site is published'}.
      </Typography>
      <Box sx={tiles(180)}>
        {records.map((record) => (
          <Box
            key={record.linkId ?? record.path.join('/')}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, minWidth: 0 }}
          >
            <PreviewNode node={record} depth={depth + 1} compact />
          </Box>
        ))}
      </Box>
    </Stack>
  )
}

/** ", laid out by the Hero pattern when the site is published", once the name is read. */
function PatternName({ id }: { id: string }) {
  const { tenant } = useContext(Reading)
  const declaration = useGetAuthoringComponentQuery({ tenant, id })
  const name = declaration.data?.label
  return <>{name ? `, laid out by the “${name}” pattern` : ', laid out by a pattern'} when the site is published</>
}

/**
 * A record as its own page: what it says, then what it owns that the
 * reading of a record does not already follow — an album's gallery,
 * where a pest's body is already drawn as its words.
 *
 * A record in a list is a card and its photos are not drawn there; here
 * the record *is* the page, so they are.
 */
function RecordReading({ root, document, layout }: { root: CompositionNode; document: Document; layout?: string }) {
  const { manifest } = useContext(Reading)
  const bodies = new Set(bodyEdges(manifest, root).map((edge) => edge.edgeId))
  const owned = new Set(ownedReferences(manifest, root.type).map((reference) => reference.name))
  const arranged = root.references.filter((edge) => owned.has(edge.name) && !bodies.has(edge.edgeId))

  return (
    <Stack spacing={3}>
      <Inline node={root} depth={0} document={document} />
      {arranged.map((edge) =>
        edge.id ? <Fetched key={edge.edgeId} id={edge.id} typeName={edge.type ?? ''} depth={1} /> : null,
      )}
      {layout && (
        <Typography variant="body2" color="text.secondary">
          Laid out by the <Named id={layout} fallback="page's" /> pattern when the site is published.
        </Typography>
      )}
    </Stack>
  )
}

/**
 * A pattern placement's own account of itself.
 *
 * The layout is the pattern's and is decided at publish; what this client
 * holds is which pattern, which records it is given, and the words the
 * author gave it — the last drawn above, by `says`. Naming the first two
 * is the difference between "something sits here" and "the Hero, about
 * Bluu Interactive".
 */
function LaidOut({ pattern, records }: { pattern: string; records: (string | undefined)[] }) {
  return (
    <Typography variant="body2" color="text.secondary">
      Laid out by the <Named id={pattern} fallback="pattern" /> pattern when the site is published
      {records.filter(Boolean).length > 0 && (
        <>
          , from{' '}
          {records.filter((id): id is string => Boolean(id)).map((id, index) => (
            <span key={id}>
              {index > 0 && ' and '}
              <Named id={id} fallback="a record" />
            </span>
          ))}
        </>
      )}
      .
    </Typography>
  )
}

function Named({ id, fallback }: { id: string; fallback: string }) {
  const { tenant } = useContext(Reading)
  const named = useGetAuthoringComponentQuery(id ? { tenant, id } : skipToken)
  const label = named.data?.label
  return <>{label ? `“${label}”` : fallback}</>
}

/**
 * A record's own picture, read through the one edge that can hold one.
 *
 * **Single-valued and accepting an upload**, which is the whole test. An
 * album's `cover` qualifies and its `gallery` does not.
 */
function coverEdge(manifest: Manifest | undefined, node: CompositionNode) {
  const declared = declaredReferences(manifest, node.type)
  return node.references.find((reference) => {
    const says = declared.find((candidate) => candidate.name === reference.name)
    if (!says || says.multiple) return false
    return says.accepts.some((accepted) => acceptsUpload(manifest, accepted))
  })
}

/** That picture, fetched and drawn. Silent where there is nothing to draw. */
function Cover({ id, compact }: { id: string; compact?: boolean }) {
  const { tenant } = useContext(Reading)
  const media = useGetAuthoringComponentQuery({ tenant, id })
  if (!media.data || !isImage(media.data)) return null
  const url = thumbnail(media.data, compact ? 480 : 1200)
  if (!url) return null
  const fields = (media.data.fields ?? {}) as Record<string, unknown>
  return <Image url={url} alt={typeof fields.alt === 'string' ? fields.alt : ''} compact={compact} />
}

/**
 * The references worth following into content, read off the manifest.
 *
 * **A reference is followed when what it accepts holds a list**, which
 * picks out exactly the repeating block's `source` without this file
 * knowing the word "source". A pattern's declaration is excluded by the
 * meta-type check — its tree is a template rather than content.
 */
function listedEdges(manifest: Manifest | undefined, node: CompositionNode) {
  const declared = declaredReferences(manifest, node.type)
  return node.references.filter((reference) => {
    const says = declared.find((candidate) => candidate.name === reference.name)
    if (!says) return false
    return says.accepts.some((accepted) => {
      const type = typeOf(manifest, accepted)
      if (!type || type.defines || type.declaresPatterns) return false
      return arranges(manifest, accepted)
    })
  })
}

/** An uploaded picture, with the caption an author wrote for it. */
function Picture({ url, node, compact }: { url: string; node: CompositionNode; compact?: boolean }) {
  const alt = typeof node.fields.alt === 'string' ? node.fields.alt : ''
  const caption = typeof node.fields.caption === 'string' ? node.fields.caption : ''
  return (
    <Box component="figure" sx={{ m: 0 }}>
      <Image url={url} alt={alt} compact={compact} />
      {caption && (
        <Typography component="figcaption" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {caption}
        </Typography>
      )}
    </Box>
  )
}

/**
 * A picture, **never drawn past its own size**.
 *
 * A two-hundred-pixel icon stretched to the column is a blurred
 * silhouette three times the size the site draws it, and it was most of
 * the height of every page that listed pests. In a tile it fills the
 * tile's square instead, cropped rather than distorted, because a grid
 * of mismatched heights is harder to scan than one of squares.
 */
function Image({ url, alt, compact }: { url: string; alt: string; compact?: boolean }) {
  return (
    <Box
      component="img"
      src={url}
      alt={alt}
      loading="lazy"
      sx={
        compact
          ? { display: 'block', width: '100%', aspectRatio: '1', objectFit: 'contain', borderRadius: 1 }
          : { display: 'block', width: 'auto', maxWidth: '100%', height: 'auto', borderRadius: 1 }
      }
    />
  )
}

/** Content the platform assembles at publish, named where it will appear. */
function NotComposed({ typeName }: { typeName: string }) {
  const { manifest } = useContext(Reading)
  const prose = proseOf(typeOf(manifest, typeName), typeName)
  return (
    <Alert severity="info" variant="outlined">
      A {prose.title.toLowerCase()} sits here. What it draws is decided when the site is published, so it is not shown
      here.
    </Alert>
  )
}

/** As deep as this goes; the rest is real and simply not drawn. */
function Elided({ typeName }: { typeName: string }) {
  const { manifest } = useContext(Reading)
  const prose = proseOf(typeOf(manifest, typeName), typeName)
  return (
    <Typography variant="body2" color="text.secondary">
      A {prose.title.toLowerCase()} is nested deeper than this reading follows.
    </Typography>
  )
}

/** A responsive grid of tiles no narrower than `width`. */
function tiles(width: number) {
  return { display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${width}px, 1fr))`, gap: 1.5 } as const
}

/** Clamp a block to a number of lines, for a card. */
function clamped(lines: number) {
  return {
    display: '-webkit-box',
    WebkitLineClamp: String(lines),
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  } as const
}

/**
 * How the rendered markup sits in the column.
 *
 * Deliberately close to nothing: margins so paragraphs are paragraphs,
 * images that cannot escape the column, and the admin's own type
 * inherited rather than restated. Every rule beyond that would be this
 * file having an opinion about how a site looks.
 */
const proseStyles = {
  '& p': { my: 1 },
  '& h1, & h2, & h3, & h4, & h5, & h6': { my: 1.5, fontWeight: 650, lineHeight: 1.3 },
  '& ul, & ol': { my: 1, pl: 3 },
  '& li': { mb: 0.5 },
  '& img': { maxWidth: '100%', height: 'auto', display: 'block' },
  '& img:not([src])': { display: 'none' },
  '& table': { borderCollapse: 'collapse', width: '100%' },
  '& td, & th': { border: 1, borderColor: 'divider', p: 0.75, textAlign: 'left' },
  '& blockquote': { my: 1, ml: 0, pl: 2, borderLeft: 3, borderColor: 'divider', color: 'text.secondary' },
  '& a': { color: 'primary.main' },
  '& :last-child': { mb: 0 },
  wordBreak: 'break-word',
} as const
